// src/pages/AdminPanel/AddProduct.jsx

import React, { useState, useEffect } from 'react';
import { db, storage } from '../../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './AddProduct.css';

const AddProduct = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [price, setPrice] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [onSale, setOnSale] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [sizes, setSizes] = useState({
    S: '', M: '', L: '', XL: '', XXL: '', OneSize: '',
    '1X': '', '2X': '', '3X': '', '4X': '' // Added new sizes
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // Effect to calculate discounted price whenever price or discount changes
  useEffect(() => {
    const calculateDiscountedPrice = () => {
      const originalPrice = parseFloat(price);
      const discount = parseFloat(discountPercent);

      if (onSale && !isNaN(originalPrice) && !isNaN(discount) && discount > 0) {
        const calculatedPrice = originalPrice * (1 - discount / 100);
        setDiscountedPrice(calculatedPrice.toFixed(2));
      } else {
        setDiscountedPrice(null);
      }
    };
    calculateDiscountedPrice();
  }, [price, discountPercent, onSale]);

  const handleSizeChange = (e) => {
    setSizes({ ...sizes, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (selectedImages.length + files.length > 5) {
      setMsg('You can only upload a maximum of 5 images.');
      return;
    }
    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setSelectedImages((prev) => [...prev, ...mapped]);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('imageIndex', index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = e.dataTransfer.getData('imageIndex');
    if (dragIndex === dropIndex) return;

    const updated = [...selectedImages];
    const [draggedItem] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, draggedItem);
    setSelectedImages(updated);
  };

  const removeImage = (index) => {
    const updated = [...selectedImages];
    updated.splice(index, 1);
    setSelectedImages(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !price || !desc || !category || !brand || selectedImages.length === 0) {
      setMsg('Please fill all required fields and upload at least one image.');
      return;
    }

    if (onSale && (!discountPercent || isNaN(parseFloat(discountPercent)))) {
      setMsg('Please enter a valid discount percentage for sale items.');
      return;
    }

    setLoading(true);
    setMsg('');

    try {
      const imageUploadPromises = selectedImages.map(async (imgObj) => {
        const imgRef = ref(storage, `productImages/${Date.now()}_${imgObj.file.name}`);
        const snapshot = await uploadBytes(imgRef, imgObj.file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
      });

      const imageUrls = await Promise.all(imageUploadPromises);
      const mainImage = imageUrls[0];
      const additionalImages = imageUrls.slice(1);

      const productData = {
        name,
        brand,
        price: parseFloat(price),
        description: desc,
        category,
        sizes: Object.fromEntries(
          Object.entries(sizes)
            .filter(([key, value]) => value !== '')
            .map(([key, value]) => [key, parseInt(value, 10)])
        ),
        isFeatured,
        onSale,
        imageUrl: mainImage,
        additionalImages,
        createdAt: serverTimestamp(),
      };

      if (onSale) {
        productData.discountPercent = parseFloat(discountPercent);
        productData.discountedPrice = parseFloat(discountedPrice);
      }

      await addDoc(collection(db, 'products'), productData);

      setMsg('Product added successfully!');
      
      // Reset form fields
      setName('');
      setBrand('');
      setPrice('');
      setDesc('');
      setCategory('');
      setIsFeatured(false);
      setOnSale(false);
      setDiscountPercent('');
      setDiscountedPrice(null);
      setSizes({
        S: '', M: '', L: '', XL: '', XXL: '', OneSize: '',
        '1X': '', '2X': '', '3X': '', '4X': ''
      });
      setSelectedImages([]);
      
    } catch (error) {
      console.error('Error adding product:', error);
      setMsg('Error adding product: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-product-container">
      <div className="add-product-header">
        <h2>Add New Product</h2>
        {msg && <p className="msg">{msg}</p>}
      </div>

      <form onSubmit={handleSubmit} className="add-product-form">
        <div className="adminForm-group">
          <label className='white-label'>
            Product Name
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className='white-label'>
            Brand
            <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)} required />
          </label>
        </div>

        <div className="adminForm-group">
          <label className='white-label'>
            Price ($)
            <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} required />
          </label>
          <label className='white-label'>
            Category
            <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} required />
          </label>
        </div>

        <label className="description-label">
          Description
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} required />
        </label>
        
        <div className="checkbox-group">
          <label className="checkbox-container">
            <input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />
            Featured Product
          </label>
          <label className="checkbox-container">
            <input type="checkbox" checked={onSale} onChange={(e) => setOnSale(e.target.checked)} />
            On Sale
          </label>
        </div>
        
        {onSale && (
          <div className="sale-group">
            <label>
              Discount (%)
              <input 
                type="number"
                min="0"
                max="100"
                value={discountPercent} 
                onChange={(e) => setDiscountPercent(e.target.value)}
              />
            </label>
            {discountedPrice && (
              <p className="discounted-price">
                Sale Price: <span>${discountedPrice}</span>
              </p>
            )}
          </div>
        )}

        <fieldset className="sizes-fieldset">
          <legend>Sizes & Stock</legend>
          <div className="sizes-grid">
            {Object.keys(sizes).map((size) => (
              <label key={size} className="size-item">
                {size}
                <input
                  type="number"
                  name={size}
                  value={sizes[size]}
                  onChange={handleSizeChange}
                  placeholder="0"
                  min="0"
                />
              </label>
            ))}
          </div>
        </fieldset>

        <label className="image-upload-label">
          Upload Images (First one = Main image)
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageChange}
            className="hidden-input-file"
          />
        </label>

        {selectedImages.length > 0 && (
          <div className="image-preview-grid">
            {selectedImages.map((img, index) => (
              <div
                key={index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, index)}
                className="image-preview-item"
              >
                <img src={img.preview} alt={`Product preview ${index + 1}`} />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="remove-image-button"
                >
                  &times;
                </button>
                {index === 0 && (
                  <span className="main-image-label">Main Image</span>
                )}
              </div>
            ))}
          </div>
        )}

        <button type="submit" disabled={loading} className="submit-button" onClick={handleSubmit}>
          {loading ? 'Adding...' : 'Add Product'}
        </button>
      </form>
    </div>
  );
};

export default AddProduct;
